import { useUser as swUseUser, useSessionContext } from '@shopware/composables'
import { ApiClientError } from '@shopware/api-client'
import { useCart } from '#imports'
import { computed } from 'vue'

export function useUser() {
    const { user, logout: swLogout, ...coreUseUser } = swUseUser()
    const { refreshSessionContext } = useSessionContext()
    const { refreshCart } = useCart()

    /*
     * Upstream logout() runs refreshSessionContext() and refreshCart() only after a
     * successful API call. When the server session is already gone the call answers 403,
     * both reconciliation steps are skipped, and the client keeps isLoggedIn === true --
     * so logout is broken in exactly the situation that needs it, and the drift can only
     * be resolved by the very call that fails.
     *
     * A 401/403 means logout's goal is already achieved: treat it as success and reconcile
     * the client state either way.
     */
    async function logout() {
        try {
            return await swLogout()
        } catch (e: unknown) {
            if (!(e instanceof ApiClientError && (e.status === 401 || e.status === 403))) {
                throw e
            }
        } finally {
            await refreshSessionContext()
            refreshCart()
        }
    }

    /*
     * Remove id and compare mapped shipping and billing address to tell if they are the same or not
     */
    const billingSameAsShipping = computed<boolean | null>(() => {
        if (user.value?.activeShippingAddress == null || user.value?.activeBillingAddress == null) {
            return null
        }

        const { id: _shippingId, ...cleanedShippingAddress } = user.value.activeShippingAddress
        const { id: _billingId, ...cleanedBillingAddress } = user.value.activeBillingAddress

        return JSON.stringify(cleanedShippingAddress) === JSON.stringify(cleanedBillingAddress)
    })

    return {
        ...coreUseUser,
        user,
        logout,
        billingSameAsShipping,
    }
}
